'use client';

import { type JSX, useCallback, useEffect } from 'react';

import { Text } from '@videoblocks/design-system';
import { Pencil } from '@videoblocks/design-system/icons';

import { ConfirmCreditSpendModal } from '../../_shared/_components/Modals/ConfirmCreditSpendModal';
import { EnterpriseFeatureAccessModal } from '../../_shared/_components/Modals/EnterpriseFeatureAccessModal';
import { EnterpriseInsufficientCreditsModal } from '../../_shared/_components/Modals/EnterpriseInsufficientCreditsModal';
import { InsufficientCreditsModal } from '../../_shared/_components/Modals/InsufficientCreditsModal';
import { RegenerateConfirmationModal } from '../../_shared/_components/Modals/RegenerateConfirmationModal';
import { AiToolkitToast } from '../../_shared/_components/Toast/AiToolkitToast';
import { UseCaseSelector } from '../../_shared/_components/UseCaseSelector/UseCaseSelector';
import { ModelSelectField } from '../../_shared/_components/forms/ModelSelectField';
import { TextAreaInputField } from '../../_shared/_components/forms/TextAreaInputField/TextAreaInputField';
import { useAiToolkitContext } from '../../_shared/_context/AiToolkitContext';
import { AI_TOOLKIT_TELEMETRY_ACTIONS, emitAiToolkitTag } from '../../_shared/_context/aiToolkitTelemetry';
import { DISABLED_INPUT_REASONS, MAX_PROMPT_LENGTH, VIDEO_EDIT_MODEL_OPTIONS } from '../../_shared/_context/constants';
import { AI_TOOLKIT_CATEGORIES, AI_USER_SEGMENTS } from '../../_shared/_context/types';
import { AiToolkitSidePanel } from '../../_shared/_layouts/AiToolkitSidePanel';
import { VIDEO_EDIT_USE_CASES } from './constants';
import { useVideoEditForm } from './useVideoEditForm';

export const VideoEditSidePanel = (): JSX.Element => {
    const {
        formState,
        promptState,
        promptHelperText,
        promptPlaceholder,
        suggestions,
        isSubmitDisabled,
        onPromptChange,
        onPromptBlur,
        onUseCaseSelect,
        onSuggestionClick,
        onSubmit,
        onConfirm,
        isValid,
        calculatedCost,
    } = useVideoEditForm();
    const {
        activeTask,
        acknowledgeTask,
        submitStates,
        dispatchConfirmationModal,
        dispatchVideoEditSubmit,
        videoSearch,
        credits,
        isEnterprise,
        categorySegments,
    } = useAiToolkitContext();

    const { status } = submitStates[AI_TOOLKIT_CATEGORIES.VIDEO_EDIT];
    const isSuccess = status === 'success';
    const isFormDisabled = !!activeTask && activeTask.status === 'inProgress';
    const isSubscribeCTA = categorySegments[AI_TOOLKIT_CATEGORIES.VIDEO_EDIT] !== AI_USER_SEGMENTS.SUBSCRIBED;
    const isEditButtonDisabled = !isSubscribeCTA && (isSubmitDisabled || isFormDisabled);
    const isModelLocked = VIDEO_EDIT_MODEL_OPTIONS.length <= 1;
    const showOtherTaskToast = activeTask !== null && activeTask.category !== AI_TOOLKIT_CATEGORIES.VIDEO_EDIT;

    useEffect(() => {
        if (status === 'success' || status === 'error') {
            acknowledgeTask(AI_TOOLKIT_CATEGORIES.VIDEO_EDIT, status);
        }
    }, [status, acknowledgeTask]);

    const handleSubmit = useCallback((): void => {
        if (calculatedCost > credits) {
            dispatchConfirmationModal({
                type: 'OPEN_MODAL',
                modal: {
                    type: isEnterprise ? 'entInsufficientCredits' : 'insufficientCredits',
                    category: AI_TOOLKIT_CATEGORIES.VIDEO_EDIT,
                },
            });
            return;
        }
        if (isSuccess && isValid) {
            emitAiToolkitTag(AI_TOOLKIT_CATEGORIES.VIDEO_EDIT, AI_TOOLKIT_TELEMETRY_ACTIONS.regenerateWithNewPrompt);
            dispatchConfirmationModal({
                type: 'OPEN_MODAL',
                modal: { type: 'regenerate', category: AI_TOOLKIT_CATEGORIES.VIDEO_EDIT },
            });
            return;
        }
        onSubmit();
    }, [calculatedCost, credits, isEnterprise, dispatchConfirmationModal, isSuccess, isValid, onSubmit]);

    return (
        <AiToolkitSidePanel
            aiToolkitCategory={AI_TOOLKIT_CATEGORIES.VIDEO_EDIT}
            onSubmit={handleSubmit}
            isSubmitDisabled={isEditButtonDisabled}
        >
            <>
                <div className="w-full flex flex-col gap-6">
                    <div className="hidden md:block shrink-0 dark:bg-neutral-dark-200 p-3 rounded-sm">
                        <Text asChild fontSize="sm" className="text-center">
                            <h2>Video Editor</h2>
                        </Text>
                    </div>
                    {isSuccess && !videoSearch.showSearch && (
                        <button
                            type="button"
                            onClick={() => dispatchVideoEditSubmit({ type: 'EDIT_SELECTION' })}
                            className="flex items-center justify-between gap-3 py-4 border-y dark:border-neutral-dark-300 cursor-pointer"
                        >
                            <div className="flex flex-col gap-1 text-left">
                                <Text fontSize="sm" fontWeight="semibold">
                                    Edit video setup
                                </Text>
                                <Text fontSize="xs" className="dark:text-neutral-dark-700">
                                    Change your video selection.
                                </Text>
                            </div>
                            <Pencil width={20} aria-hidden="true" className="shrink-0 dark:[&_path]:fill-white" />
                        </button>
                    )}
                    <UseCaseSelector
                        useCases={VIDEO_EDIT_USE_CASES}
                        selectedUseCaseId={formState.useCase}
                        onSelect={onUseCaseSelect}
                        disabled={isFormDisabled}
                    />
                    <TextAreaInputField
                        placeholder={promptPlaceholder}
                        maxLength={MAX_PROMPT_LENGTH}
                        value={formState.prompt}
                        state={promptState}
                        helperText={promptHelperText}
                        onChange={onPromptChange}
                        onBlur={onPromptBlur}
                        disabled={isFormDisabled}
                        suggestions={suggestions}
                        onSuggestionClick={onSuggestionClick}
                    />
                    <ModelSelectField
                        modelValue={formState.model}
                        modelVersion={VIDEO_EDIT_MODEL_OPTIONS[0].version}
                        modelName={VIDEO_EDIT_MODEL_OPTIONS[0].name}
                        modelDescription={VIDEO_EDIT_MODEL_OPTIONS[0].description}
                        className="w-full"
                        disabled={isModelLocked}
                        disabledReason={isModelLocked ? DISABLED_INPUT_REASONS.singleModel : undefined}
                    />
                </div>
                <AiToolkitToast open={showOtherTaskToast} activeTask={activeTask} />
                <ConfirmCreditSpendModal onConfirm={onConfirm} />
                <InsufficientCreditsModal />
                <EnterpriseInsufficientCreditsModal />
                <EnterpriseFeatureAccessModal />
                <RegenerateConfirmationModal onConfirm={onConfirm} />
            </>
        </AiToolkitSidePanel>
    );
};
